<template>
  <div class="admin-container">
    <div class="admin-header">
      <h1>Content Logs</h1>
    </div>
    <div class="border-container-unique37">
      <div class="admin-list-title">List of Content Logs</div>
      <div>You can view all of Content Logs from this page.</div>

      <div class="admin-flex-container" ref="adminFlexContainer">
        <div class="admin-search-container">
          <input
            type="text"
            class="admin-search-input"
            placeholder="Search..."
            v-model="searchQuery"
            @input="handleSearchChange"
          />
          <img
            src="/images/maker-cart/union.svg"
            alt="Search"
            class="admin-search-icon"
          />
        </div>
      </div>
      <div class="admin-list-tool-container"></div>

      <div class="mb-3" style="display: flex; gap: 10px; flex-wrap: wrap">
        <button
          v-for="(button, index) in buttons"
          :key="index"
          :class="{ 'checkbox-button-clicked': button.clicked }"
          @click="toggleClicked(index)"
          class="checkbox-button"
        >
          {{ button.label }}
        </button>
      </div>

      <TableData
        :headers="headers"
        :items="tableItems"
        :loading="tableLoading"
        :total-pages="data?.data?.total_pages || 0"
        :current-page="currentPage"
        @update:current-page="changePage"
        :is-action="false"
        :use-api-sort="true"
        @sort="handleTableSort"
        :customActions="[]"
        :show-data-info="true"
        :total-rows="data?.data?.total_rows || 0"
        :current-limit="filters.limit"
        variant="rounded"
      />
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { onMounted, ref, computed, watch } from "vue";
import PostLogAdminService from "@/services/postLogAdminService";
import { debounce } from "lodash";
import TableData from "@/components/common/TableData.vue";
import { useUrlQuerySync } from "@/composeables/useUrlQuerySync";

const tableLoading = ref(true);
const searchQuery = ref("");
const currentStatus = ref("");
const buttons = ref([
  { label: "All", clicked: true, status: "" },
  { label: "Create", clicked: false, status: "create" },
  { label: "Edit", clicked: false, status: "edit" },
  { label: "Delete", clicked: false, status: "delete" },
  { label: "Copy", clicked: false, status: "copy" },
]);

const data = ref({
  message: "",
  error: null,
  data: {
    page: 1,
    limit: 10,
    total_rows: 0,
    total_pages: 0,
    rows: [],
  },
});

const currentPage = ref(1);
const filters = ref({
  search: "",
  page: 1,
  limit: 10,
  key: "",
  sort_by: "datetime",
  order: "desc",
});

useUrlQuerySync({
  filters,
  excludeDefaults: [{ page: 1 }, { limit: 10 }],
});

onMounted(async () => {
  await getAll();
  window.addEventListener("resize", updateLayout);
  updateLayout();
});

// Define table headers
const headers = ref([
  { text: "Key", value: "key", sortable: false },
  { text: "Content Type", value: "post_type", sortable: false },
  { text: "Name", value: "name", sortable: false },
  { text: "Email", value: "email", sortable: false },
  { text: "Title", value: "post_title", sortable: false },
  { text: "Date", value: "datetime", sortable: true },
]);

// Map API data to table items
const tableItems = computed(() => {
  return (data.value?.data?.rows || []).map((item) => ({
    id: item.id || `${item.key}-${item.datetime}`,
    key: item.key || "-",
    post_type: item.post_type || "-",
    name: `${item.user?.firstname || "-"} ${item.user?.lastname || ""}`.trim(),
    email: item.user?.email || "-",
    post_title: parseSerializedString(item.data).post_title || "-",
    datetime: convertTimestamp(item.datetime) || "-",
  }));
});

const getAll = async () => {
  try {
    console.log("postactivity");
    tableLoading.value = true;

    const response = await PostLogAdminService.getAll(filters.value);
    if (response.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    console.log(error.message);
  } finally {
    tableLoading.value = false;
  }
};

// Sync search query with filters.search
watch(
  () => filters.value.search,
  (newValue) => {
    searchQuery.value = newValue || "";
  }
);

// Debounced search handler
const handleSearchChange = debounce(() => {
  filters.value.search = searchQuery.value;
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
}, 300);

const changePage = (page) => {
  currentPage.value = page;
  filters.value.page = page;
  getAll();
};

const updateVisibilityModalDelete = () => {
  showModalDelete.value = !showModalDelete.value;
};

// Function to update layout based on window width
const updateLayout = () => {
  const container = document.querySelector(".admin-flex-container");
  const toolContainer = document.querySelector(".admin-list-tool-container");
  const parentContainer = document.querySelector(".border-container-unique37");
  const width = window.innerWidth;

  if (width <= 991) {
    parentContainer.insertBefore(toolContainer, container.nextSibling);
  } else {
    container.appendChild(toolContainer);
    container.insertBefore(toolContainer, container.children[1]);
  }
};

// Toggle filter buttons
const toggleClicked = (index) => {
  buttons.value.forEach((button, idx) => {
    button.clicked = idx === index;
  });
  currentStatus.value = buttons.value[index].status;
  filters.value.key = currentStatus.value;
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
};

// Keep buttons in sync with URL (filters.key)
watch(
  () => filters.value.key,
  (newKey) => {
    currentStatus.value = newKey || "";
    buttons.value.forEach((button) => {
      button.clicked = button.status === currentStatus.value;
    });
  },
  { immediate: true }
);

function parseSerializedString(serializedString) {
  const regex = /s:\d+:"([^"]+)";s:\d+:"([^"]+)";/g;
  let match;
  const result = {};

  while ((match = regex.exec(serializedString)) !== null) {
    if (match.index === regex.lastIndex) {
      regex.lastIndex++;
    }

    const key = match[1];
    const value = match[2];

    if (key === "user_display") {
      result.user_display = value;
    } else if (key === "post_title") {
      result.post_title = value;
    }
  }

  return result;
}

// Handle sorting from TableData
const handleTableSort = ({ sort_by, sort_dir }) => {
  filters.value.sort_by = sort_by;
  filters.value.order = sort_dir.toLowerCase();
  filters.value.page = 1;
  currentPage.value = 1;
  getAll();
};

function convertTimestamp(timestamp) {
  // Buat objek Date dari timestamp
  var date = new Date(timestamp * 1000);

  // Format tanggal dan waktu
  var year = date.getFullYear();
  var month = ("0" + (date.getMonth() + 1)).slice(-2);
  var day = ("0" + date.getDate()).slice(-2);
  var hours = ("0" + date.getHours()).slice(-2);
  var minutes = ("0" + date.getMinutes()).slice(-2);
  var seconds = ("0" + date.getSeconds()).slice(-2);

  // Gabungkan hasil format
  var formattedDate =
    year +
    "-" +
    month +
    "-" +
    day +
    " " +
    hours +
    ":" +
    minutes +
    ":" +
    seconds;
  return formattedDate;
}
</script>
